import { flushSync } from "react-dom";
import type { TimelineWriteOnlyContext } from "../../vendor/src/features/remotion-editor/editor/context-provider";
import { insertPreparedMedia, type InsertionTarget } from "./incoming-media";
import type { EditorStarterItem } from "../../vendor/src/features/remotion-editor/editor/items/item-type";
import type { EditorStarterAsset } from "../../vendor/src/features/remotion-editor/editor/assets/assets";
import type { UndoableState } from "../../vendor/src/features/remotion-editor/editor/state/types";

const messages = {
  "pending-edit": "이동이나 시간 입력을 마친 뒤 다시 추가해 주세요.",
  "invalid-media": "원본 파일의 종류와 재생 길이를 확인해 주세요.",
  "id-conflict":
    "원본을 덮어쓰지 않도록 추가를 멈췄습니다. 다시 추가해 주세요.",
  "target-removed":
    "추가할 트랙이 변경됐습니다. 원하는 위치에 다시 놓아 주세요.",
  "placement-rejected": "같은 종류의 빈 트랙이나 새 트랙에 놓아 주세요.",
  "fps-changed":
    "파일을 읽는 동안 프레임 속도가 바뀌었습니다. 다시 추가해 주세요.",
};
/** Call after asynchronous file preparation, never from a render or lifecycle callback. */
export function commitIncomingMedia(
  context: TimelineWriteOnlyContext,
  item: EditorStarterItem,
  asset: EditorStarterAsset,
  target: InsertionTarget,
  fps: number,
  expectedUndoableState?: UndoableState,
): void {
  // Settle any queued interaction before reading its final document, then plan outside React's updater.
  flushSync(() => {});
  const base = context.getCurrentState?.();
  if (!base || !base.initialized || (expectedUndoableState && base.undoableState !== expectedUndoableState))
    throw new Error("편집 화면이 변경됐습니다. 다시 추가해 주세요.");
  const result = insertPreparedMedia(base, item, asset, target, fps);
  if (!result.ok) throw new Error(messages[result.reason]);
  let applied = false;
  flushSync(() =>
    context.setState({
      update: (current) => {
        if (
          current.undoableState !== base.undoableState ||
          current.timelineInputOwner !== base.timelineInputOwner ||
          current.timelineMoveActive !== base.timelineMoveActive
        )
          return current;
        applied = true;
        return {
          ...current,
          undoableState: result.state.undoableState,
          assetStatus: result.state.assetStatus,
          selectedItems: result.state.selectedItems,
          timelineTimeEditApplied: true,
        };
      },
      commitToUndoStack: true,
      ...(expectedUndoableState && { expectedUndoableState }),
    }),
  );
  if (!applied)
    throw new Error(
      "편집 내용이 바뀌어 추가를 멈췄습니다. 다시 추가해 주세요.",
    );
}
